// Durable outbox for ranked and challenge wins that failed to submit; replayed when the backend is back.
// Replays are safe (server guards with `was_done`); one landing after the day/week rolled over gets a 4xx and is dropped.

const KEY = 'wl_submit_outbox';
const MAX_ITEMS = 50;
const MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

function read() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY));
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}

function write(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items.slice(-MAX_ITEMS)));
  } catch {
    /* localStorage full/unavailable — best effort */
  }
}

// Items queued before challenge support have no kind; they are ladder wins.
function kindOf(it) {
  return it.kind || 'ladder';
}

function sig(it) {
  return `${kindOf(it)}:${it.lang}:${it.gameIndex}:${JSON.stringify(it.steps)}`;
}

// The server answered and refused it (bad solution, week over); retrying cannot help.
export function isPermanentRejection(err) {
  const s = err && err.status;
  return s >= 400 && s < 500 && ![401, 403, 408, 429].includes(s);
}

// Queue an undelivered win; `kind` is 'ladder' or 'challenge'. De-duplicated by (kind, lang, gameIndex, steps).
export function enqueueSubmit(kind, item) {
  if (!item || item.gameIndex == null || !item.steps) return;
  const entry = { ...item, kind };
  const items = read();
  const s = sig(entry);
  if (items.some((i) => sig(i) === s)) return;
  items.push({ ...entry, ts: Date.now() });
  write(items);
}

let flushing = false;

// Deliver queued wins via `submitFns` (kind → api submit fn); only transient failures stay queued.
export async function flushSubmitOutbox(submitFns) {
  if (flushing) return;
  const items = read();
  if (!items.length) return;
  flushing = true;
  try {
    const remaining = [];
    const now = Date.now();
    for (const it of items) {
      const submitFn = submitFns[kindOf(it)];
      if (!submitFn || (it.ts && now - it.ts > MAX_AGE_MS)) continue;
      try {
        await submitFn(it.lang, it.gameIndex, it.steps, it.elapsed, it.distanceIndicatorUsed);
      } catch (err) {
        if (!isPermanentRejection(err)) remaining.push(it); // keep for the next flush
      }
    }
    // Merge with anything enqueued while this flush was in flight.
    const sent = new Set(items.map(sig));
    write([...remaining, ...read().filter((i) => !sent.has(sig(i)))]);
  } finally {
    flushing = false;
  }
}

export function outboxSize() {
  return read().length;
}
